import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ChevronRight } from 'lucide-react';
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import ProvenResults from '@/components/shared/ProvenResults';

const Terms = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Use the shared Header component */}
      <Header />
      
      {/* Main content */}
      <main className="flex-1">
        {/* Hero section */}
        <section className="bg-roundone-dark-purple py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-roundone-white/70 mb-6">
                <Link to="/" className="hover:text-white">Home</Link>
                <ChevronRight className="h-4 w-4 text-roundone-white/70" />
                <span className="text-white">Terms & Conditions</span>
              </div>
              <h1 className="text-4xl font-bold mb-6 text-white">Terms & Conditions</h1>
              <p className="text-roundone-white/80">
                Last updated: May 15, 2023
              </p>
            </div>
          </div>
        </section>
        
        {/* Terms content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto prose prose-lg">
              <p>Please read these Terms and Conditions ("Terms", "Terms and Conditions") carefully before using the RoundOne AI platform operated by RoundOne AI, Inc. ("RoundOne", "we", "us", or "our").</p>
              
              <p>Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who access or use the Service.</p>
              
              <p>By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the Service.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">1. Use of Service</h2>
              
              <p>RoundOne AI provides an AI-powered candidate assessment platform ("Service") designed to help evaluate job candidates. You may use this Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:</p>
              
              <ul className="list-disc pl-6 mb-6">
                <li>In any way that violates any applicable federal, state, local, or international law or regulation.</li>
                <li>To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Service.</li>
                <li>To impersonate or attempt to impersonate RoundOne, a RoundOne employee, another user, or any other person or entity.</li>
                <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which, as determined by us, may harm RoundOne or users of the Service or expose them to liability.</li>
              </ul>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">2. Accounts</h2>
              
              <p>When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.</p>
              
              <p>You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service.</p>
              
              <p>You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">3. Subscription and Payments</h2>
              
              <p>Some features of the Service are available on a subscription basis. You will be billed in advance on a recurring and periodic basis (such as monthly or annually), depending on the type of subscription plan you select when purchasing a subscription.</p>
              
              <p>At the end of each period, your subscription will automatically renew under the exact same conditions unless you cancel it or RoundOne cancels it. You may cancel your subscription renewal either through your online account management page or by contacting our customer support team.</p>
              
              <p>A valid payment method, including credit card, is required to process the payment for your subscription. You shall provide RoundOne with accurate and complete billing information including full name, address, state, zip code, telephone number, and valid payment method information. By submitting such payment information, you automatically authorize RoundOne to charge all subscription fees incurred through your account to any such payment instruments.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">4. Intellectual Property</h2>
              
              <p>The Service and its original content, features, and functionality are and will remain the exclusive property of RoundOne and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of RoundOne.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">5. User Data</h2>
              
              <p>We collect and process personal data about you and your candidates when you use the Service. Our Privacy Policy describes how we handle the information you provide to us.</p>
              
              <p>You own all of your data, and we will only use it as permitted in these Terms and described in our Privacy Policy. You are responsible for ensuring that you have all necessary permissions to collect, process, and share candidate data using our Service, and that your use of our Service complies with applicable data protection laws.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">6. Termination</h2>
              
              <p>We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.</p>
              
              <p>Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service, or notify us that you wish to delete your account.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">7. Limitation of Liability</h2>
              
              <p>In no event shall RoundOne, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; (iii) any content obtained from the Service; and (iv) unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">8. Disclaimer</h2>
              
              <p>Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement or course of performance.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">9. Governing Law</h2>
              
              <p>These Terms shall be governed and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions.</p>
              
              <p>Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">10. Changes to Terms</h2>
              
              <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
              
              <p>By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.</p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4">11. Contact Us</h2>
              
              <p>If you have any questions about these Terms, please contact us at legal@roundone.ai</p>
            </div>
          </div>
        </section>
        
        {/* Add ProvenResults component */}
        <ProvenResults />
      </main>
      
      {/* Use the shared Footer component */}
      <Footer />
    </div>
  );
};

export default Terms;
